import clsx from 'clsx'
import { rem } from 'polished'
import * as React from 'react'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Typography from 'LuxKit/Typography/Typography'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import noop from 'lib/function/noop'
import { mediaHoverable } from 'lib/theme/mediaQueries'

import type { AccommodationDayView } from './AccommodationCalendar.types'

const Cell = styled(Clickable)`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${rem(4)} ${rem(8)};
  transition: background-color 0.2s;
  min-height: ${rem(72)};

  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
  1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
  1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset,
  0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset;

  &.type-check-in, &.type-check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color:  ${props => props.theme.palette.highlight.primary.normalContrast};
  }

  &.type-between {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    color:  ${props => props.theme.palette.highlight.primary.lightContrast};
  }

  &.type-none.variant-unavailable {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    }
  }
`

const Content = styled(Typography)`
  opacity: 0;

  &.visible {
    opacity: 1;
  }
`

const DayRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(4)};
`

export type AccommodationCalendarDayCellType = 'check-in' | 'check-out' | 'between' | 'none'
export type AccommodationDayCellVariant = 'price-member' | 'price-cheapest' | 'price' | 'none' | 'sold-out-extra-action' | 'unavailable' | 'availability-available' | 'availability-search'

export type AccommodationDayCellSelectHandler = (day: AccommodationDayView) => void

interface Props {
  /** The raw calendar data for a day */
  day: AccommodationDayView
  /**
   * Event for when the cell is clicked, provides the day object to the listener
   */
  onClick?: AccommodationDayCellSelectHandler
  /**
   * The purpose the cell is for, determines which components
   * in the cell are shown
   */
  variant: AccommodationDayCellVariant
  /**
   * The 'state' the cell is currently in
   * Determines mostly how the *cell* looks
   */
  type: AccommodationCalendarDayCellType
  style?: React.CSSProperties
  /**
   * Users current loyalty tier
   * Passed in for performance reasons so each cell isn't listening
   * on state
   */
  tier?: App.LoyaltyTier
}

function AccommodationCalendarLargeDayCell(props: Props) {
  const { day, onClick = noop, variant, type, style, tier } = props

  const priceColour = useMemo<React.ComponentProps<typeof Caption>['colour']>(() => {
    if (variant === 'price-member') {
      return 'lux-plus'
    } else if (variant === 'price-cheapest' && type !== 'check-in') {
      return 'highlight-primary-dark'
    }
    return 'light-foreground-alt'
  }, [variant, type])

  const showContent = type !== 'between' && type !== 'check-out'
  const isAvailabilityMode = variant === 'availability-available' || variant === 'availability-search'
  const isSoldOutDayDisabled = !isAvailabilityMode && day.soldOut && !day.soldOutExtraActionText

  const price = day.pricing?.price ?? 0
  return <Cell
    style={style}
    disabled={variant === 'unavailable' || isSoldOutDayDisabled}
    onClick={() => onClick(day)}
    className={clsx(
      `type-${type}`,
      `variant-${variant}`,
    )}
    data-testid={`day-${day.date}`}
  >
    <DayRow>
      <BodyText variant="medium">{day.day}</BodyText>
      {day.featured && type !== 'between' && <>
        {day.featured === 'exclusive' && <SolidStarCircleIcon colour="exclusive" size="XS" />}
        {day.featured === 'special' && <SolidFireIcon colour="urgency" size="XS" />}
        {day.featured === 'loyalty' && tier && <SolidCircleIcon colour={`tier-${tier}`} size="XS" />}
      </>}
      {variant === 'availability-available' && <SolidCheckCircleIcon colour="highlight-primary-dark" size="XS" />}
      {variant === 'availability-search' && <LineSearchIcon colour="light-foreground-alt" size="XS" />}
    </DayRow>
    <Content className={clsx({ visible: showContent })}>
      {(variant === 'price' || variant === 'price-cheapest' || variant === 'price-member') && <BodyText
        variant="medium"
        colour={priceColour}
      >
        {day.conditionalPrice ?
            <FormattedMessage
              id="BubDwLd7a81-HsviPhBn"
              defaultMessage="{price}*"
              description="Calendar day cell price with asterisk for conditional pricing"
              values={{
                price: <FormatCurrency value={price} format="roundedDollar" />,
              }}
            /> :
            <FormatCurrency value={price} format="roundedDollar" />}
      </BodyText>}
      {variant === 'sold-out-extra-action' && day.soldOutExtraActionText && <Caption
        variant="large"
        colour="light-foreground-alt"
        underline
      >
        {day.soldOutExtraActionText}
      </Caption>}
    </Content>
  </Cell>
}

export default React.memo(AccommodationCalendarLargeDayCell)
